/**
 * Position colours — one source for every surface that renders a bill position.
 *
 * Two things changed here, both decided rather than swept:
 *
 * 1. The colour language is green/red. It used to be blue for Support and orange
 *    for Oppose, with green and red reserved for the STRONG variants — so the
 *    intuitive reading of green and red was spent on the rarer values, and
 *    CandidateTable had quietly grown its own green/red version alongside. Support
 *    is green and Oppose is red; intensity is carried by fill weight, not by hue.
 *
 * 2. The values are the app's palette. This file used raw Tailwind `purple-100`,
 *    `blue-100`, `green-100`, `orange-100` and `red-100` — none of which are in
 *    the palette, and `orange` collides with `action`, the civic-action colour
 *    P1-08 reserves for one primary CTA per screen. Every value below now matches
 *    the corresponding <Chip> tone exactly, so a position badge and a position
 *    chip cannot drift apart.
 *
 * Contrast: every pair below is one of the tone/emphasis combinations measured in
 * a browser for P2-07 — light soft 4.51-10.07, dark soft 5.28-7.98, solid
 * 4.83-10.89. All clear WCAG AA for text at badge sizes.
 */

export type BillPositionValue =
  | 'Monitor'
  | 'Support'
  | 'StrSupport'
  | 'Oppose'
  | 'StrOppose'
  | 'Amend'
  | 'Pending'
  | 'Neutral';

/**
 * The tone each position maps to, kept separate from the class strings so the
 * three getters below cannot disagree with each other — which is exactly how this
 * file's bg-only and text-only variants had already drifted from its combined one.
 */
type PositionTone =
  | 'support'
  | 'strSupport'
  | 'oppose'
  | 'strOppose'
  | 'amend'
  | 'monitor'
  | 'muted';

const TONE_BY_POSITION: Record<string, PositionTone> = {
  support: 'support',
  strsupport: 'strSupport',
  'strong support': 'strSupport',
  oppose: 'oppose',
  stroppose: 'strOppose',
  'strong oppose': 'strOppose',
  amend: 'amend',
  monitor: 'monitor',
  neutral: 'muted',
  pending: 'muted',
};

/** Matches <Chip>'s tone × emphasis pairs. Change both together or not at all. */
const CLASSES: Record<PositionTone, { bg: string; text: string }> = {
  // Strong positions take the solid fill — the same one Chip uses for
  // emphasis="solid", at the 600/700 step because white on the 500s fails AA.
  strSupport: { bg: 'bg-success-700', text: 'text-white' },
  strOppose: { bg: 'bg-error-600', text: 'text-white' },

  support: {
    bg: 'bg-success-100 dark:bg-success-900/20',
    text: 'text-success-700 dark:text-success-300',
  },
  oppose: {
    bg: 'bg-error-100 dark:bg-error-900/20',
    text: 'text-error-700 dark:text-error-300',
  },
  amend: {
    bg: 'bg-warning-100 dark:bg-warning-900/20',
    text: 'text-warning-700 dark:text-warning-300',
  },
  // Monitor keeps a brand tint rather than folding into the greys. It is not a
  // stance, but it is not "nothing" either — it means someone is watching this,
  // and collapsing it into Neutral/Pending would lose that distinction.
  monitor: {
    bg: 'bg-primary-tint dark:bg-primary-900/20',
    text: 'text-primary-700 dark:text-primary-300',
  },
  // Neutral, Pending, and anything unrecognised: no stance, no colour.
  muted: {
    bg: 'bg-background-tertiary dark:bg-background-dark-tertiary',
    text: 'text-text-secondary dark:text-text-dark-secondary',
  },
};

const toneFor = (position: string): PositionTone =>
  TONE_BY_POSITION[position?.toLowerCase().trim()] ?? 'muted';

/** Background and text together — the common case. */
export const getPositionColorClasses = (position: string): string => {
  const { bg, text } = CLASSES[toneFor(position)];
  return `${bg} ${text}`;
};

/** Background only, for components that style their own text. */
export const getPositionBgClass = (position: string): string => CLASSES[toneFor(position)].bg;

/** Text only, for components that style their own background. */
export const getPositionTextClass = (position: string): string => CLASSES[toneFor(position)].text;

/**
 * Gets a human-readable display label for a position
 */
export const getPositionLabel = (position: string): string => {
  const normalizedPosition = position?.toLowerCase().trim();

  switch (normalizedPosition) {
    case 'strsupport':
      return 'Strong Support';
    case 'stroppose':
      return 'Strong Oppose';
    default:
      return position;
  }
};
